import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { CloseButton } from 'js/utils/components';
import { Container, UpgradeButton } from './common';
import Timer from './Timer';
import useDiscountDuration from './useDiscountDuration';
import * as Dismissibles from 'js/utils/dismissibles';
import {
  useFeatureGate,
  useParameterStore,
  useStatsigClient,
} from '@statsig/react-bindings';
import { useStatsigExperiment, withStatsigClient } from 'js/utils/statsig';
import _Group from "SeasonalDiscountUnibrow-Group.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/Unibrows/SeasonalDiscountUnibrow.tsx?styleId=Group";
import _Message from "SeasonalDiscountUnibrow-Message.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/Unibrows/SeasonalDiscountUnibrow.tsx?styleId=Message";
import _Subtitle from "SeasonalDiscountUnibrow-Subtitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/Unibrows/SeasonalDiscountUnibrow.tsx?styleId=Subtitle";
import _Title from "SeasonalDiscountUnibrow-Title.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/Unibrows/SeasonalDiscountUnibrow.tsx?styleId=Title";
import _MinibrowHider from "SeasonalDiscountUnibrow-MinibrowHider.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/Unibrows/SeasonalDiscountUnibrow.tsx?styleId=MinibrowHider";


type Props = {
  // Used to make sure the discount is applied at checkout
  couponCode?: string;
  lctDiscountVariant?: string;
  endDate?: string;
  feature: string;
  trigger: string;
  trialDays?: number;
  trialPrice?: string;
  couponAmount?: number;
  discountCopy?: string;
  limitedTimeCopy?: string;
  endingSoonCopy?: string;
  hideUpgradeButton?: boolean;
  ctaCopy?: string;
  hideTimer?: boolean;
};

type MinibrowProps = {
  className?: string;
  // End month and day as a timestamp or in the form "MM/DD", no year
  endDate?: string;
  endingSoonCopy?: string;
  style?: object;
  hideOnBigScreen?: boolean;
};

const Group = /*#__PURE__*/styled("div", null, {
  displayName: "Group",
  styles: _Group
});

const Message = /*#__PURE__*/styled("h3", null, {
  displayName: "Message",
  styles: _Message
});

const Subtitle = /*#__PURE__*/styled("h2", null, {
  displayName: "Subtitle",
  styles: _Subtitle
});

const Title = /*#__PURE__*/styled("h1", null, {
  displayName: "Title",
  styles: _Title
});

const MinibrowHider = /*#__PURE__*/styled("div", null, {
  displayName: "MinibrowHider",
  styles: _MinibrowHider
});

export const SeasonalDiscountMinibrow = ({
  className,
  endDate,
  endingSoonCopy = 'Offer ends soon',
  style,
  hideOnBigScreen = false,
}: MinibrowProps) => {
  const duration = useDiscountDuration(endDate);

  useEffect(() => {
    Dismissibles.recordShow('SeasonalDiscountMinibrow');
  }, []);

  style = {
    background: '#0954d3',
    height: '85px',
    padding: '12px',
    display: 'flex',
    ...style,
  };

  return (
    <MinibrowHider hideOnBigScreen={!!hideOnBigScreen}>
      <div className={className} style={style}>
        <div style={{ paddingTop: '15px' }}>
          <Subtitle>Limited Time</Subtitle>
          <Title mini>{endingSoonCopy}</Title>
        </div>
        <Group>
          <Timer startDuration={duration} mini />
        </Group>
      </div>
    </MinibrowHider>
  );
};

const SeasonalDiscountUnibrow = ({
  couponCode,
  hideUpgradeButton,
  lctDiscountVariant,
  endDate,
  feature,
  trigger,
  trialDays,
  trialPrice,
  couponAmount,
}: Props) => {
  const [show, setShow] = useState(true);
  const duration = useDiscountDuration(endDate);
  const statsigClient = useStatsigClient();
  const gate = useFeatureGate('lct_autotune_gate');
  const parameterStore = useParameterStore('academia');
  const isLctNoDiscount = lctDiscountVariant === 'lct_no_discount';
  const prefix = isLctNoDiscount
    ? 'seasonal_discount_unibrow_lct'
    : 'seasonal_discount_unibrow_discount';

  const copy_defaults = {
    headline: parameterStore.get(
      `${prefix}_discount_copy`,
      isLctNoDiscount
        ? `Academia Premium {TRIAL_DAYS} day trial for just {TRIAL_PRICE}`
        : `{COUPON_PERCENT}% OFF ACADEMIA PREMIUM`,
    ),
    subheader: parameterStore.get(
      `${prefix}_limited_time_copy`,
      isLctNoDiscount ? 'Don&rsquo;t forget:' : 'Limited Time',
    ),
    endingSoonCopy: isLctNoDiscount
      ? ''
      : parameterStore.get(`${prefix}_ending_soon_copy`, 'OFFER ENDS SOON'),
    ctaCopy: parameterStore.get(
      `${prefix}_cta_copy`,
      isLctNoDiscount ? `Upgrade now for {TRIAL_PRICE}` : 'Upgrade Now',
    ),
    backgroundColor: parameterStore.get(
      `${prefix}_container_background`,
      '#0954d3',
    ),
  };

  const experiment = useStatsigExperiment(
    'LCT Unibrow-Test 1',
    copy_defaults,
    // if we need the old discount or the feature gate does not place them in autotune, fallback to defaults.
    gate.value && isLctNoDiscount,
  );

  const interpolateCopy = (copy: string) => {
    const unescaped =
      new DOMParser().parseFromString(copy, 'text/html').body.textContent ?? '';
    return unescaped
      .replace('{TRIAL_DAYS}', trialDays?.toString() ?? '')
      .replace('{TRIAL_PRICE}', trialPrice ?? '')
      .replace('{COUPON_PERCENT}', couponAmount?.toString() ?? '');
  };

  const discountCopy = interpolateCopy(experiment.getParam('headline'));

  const limitedTimeCopy = interpolateCopy(experiment.getParam('subheader'));

  const endingSoonCopy = interpolateCopy(experiment.getParam('endingSoonCopy'));

  const ctaCopy = interpolateCopy(experiment.getParam('ctaCopy'));

  const containerBackground = experiment.getParam('backgroundColor');

  const hideTimer = isLctNoDiscount;

  useEffect(() => {
    Dismissibles.recordShow('SeasonalDiscountUnibrow');
  }, []);

  useEffect(() => {
    statsigClient.logEvent('seasonalDiscountUnibrowShown');
  }, [statsigClient]);

  const clickThrough = () => {
    statsigClient.logEvent('seasonalDiscountUnibrowCtaClicked');
    Dismissibles.recordClickthrough('SeasonalDiscountUnibrow');
  };

  const hide = () => {
    statsigClient.logEvent('seasonalDiscountUnibrowDismissed');
    Dismissibles.recordDismissal('SeasonalDiscountUnibrow');
    setShow(false);
  };

  if (!show) return null;

  const url = couponCode
    ? routes.upgrade_url({
        feature,
        trigger,
        premium_discount_coupon: couponCode,
      })
    : routes.upgrade_url({
        feature,
        trigger,
      });

  return (
    <Container variantB style={{ background: containerBackground }}>
      <div>
        <Subtitle>{limitedTimeCopy}</Subtitle>
        <Group>
          {/* Discount amount for seasonal discount is hardcoded to 50% */}
          {/* If that changes, this copy should be changed */}
          <Title>{discountCopy}</Title>
          {!hideUpgradeButton && !hideTimer && (
            <UpgradeButton
              as="a"
              href={url}
              onClick={clickThrough}
              ctaCopy={ctaCopy}
            />
          )}
        </Group>
      </div>
      {!hideTimer && (
        <Group>
          <Message>{endingSoonCopy}</Message>
          <Timer startDuration={duration} />
        </Group>
      )}
      {!hideUpgradeButton && hideTimer && (
        <UpgradeButton
          as="a"
          href={url}
          onClick={clickThrough}
          ctaCopy={ctaCopy}
        />
      )}
      <CloseButton onClick={hide} right={0} top={-4} />
    </Container>
  );
};

export default withStatsigClient(SeasonalDiscountUnibrow);
